import cogoToast from '@successtar/cogo-toast'
import { useSessionStateStore, useSessionReset } from '@renderer/stores/sessionState'
import { COUNTDOWN_PLACEHOLDER, isCountdownUrgent, useCountdown } from '@renderer/utils/countdownUtils'
import {
  AUTO_RESET_SECONDS,
  EXPIRY_GRACE_POLL_MS,
  canCheckAgainManually,
  isAnswerForCurrentOrder,
  shouldCreateNewCharge,
  shouldKeepWatchingAfterExpiry
} from '@renderer/utils/paymentRecovery'
import {
  canLeaveAfterCancel,
  connectedTerminalProvider,
  isConnectedCardReady,
  isPointRetryable,
  normalizePointStatus,
  pointCountdownAction,
  pointStatusMessage,
  terminalSerial,
  type PointCancelOutcome,
  type PointOrderStatus
} from '@renderer/utils/pointPayment'
import {
  forgetOpenTerminalOrder,
  rememberOpenTerminalOrder,
  POINT_ORDER_EXPIRY_MARGIN_MS
} from '@renderer/utils/openTerminalOrders'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import MidtransUtils, { VoucherOnlyRegionError } from '@renderer/utils/midtransUtils'
import { useInterval } from '@renderer/utils/useInterval'
import Spinner from '@renderer/components/Spinner'
import QrCodeImage from '@renderer/components/QrCodeImage'
import { useNavigate, useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import { FaUndo, FaCreditCard } from 'react-icons/fa'
import { Package } from '@renderer/types/configState'
import { useTranslation } from 'react-i18next'
import { TransactionStatus } from '@renderer/types/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import PinModal from '@renderer/components/PinModal'
import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { useDesignLayout, useSessionLayout } from '@renderer/hooks'
import Button from '@renderer/components/Button'
import { formatAmount, normalizeCurrency, roundMoney } from '@renderer/utils/formatCurrency'
import {
  guestRailFor,
  normalizePaymentProvider,
  type PaymentProvider
} from '@renderer/utils/paymentProvider'
import {
  closeToyyibpayBill,
  hasToyyibpayBillApi,
  openToyyibpayBill
} from '@renderer/lib/runtime/bridge'
import { qrActivityLogger } from '@renderer/utils/qrLoadReporter'

// Prices are stored natively (RM for MY projects). The ringgit figure is shown
// as-is; for the Midtrans cross-border QRIS rail (which settles in IDR) the
// actual rupiah charge comes back from the payment API and is shown as a hint.
// ToyyibPay bills MYR directly, so it shows ringgit only.

/**
 * How the booth collects the money for a charge, as `/api/payments` reports it.
 * `terminal` is the card terminal standing beside the booth: Studio pushed the
 * amount to it, so there is nothing on screen to scan.
 */
type PaymentType = 'core' | 'snap' | 'redirect' | 'terminal'

const PaymentPage: React.FC = () => {
  const { t } = useTranslation()
  const { isCompact: paymentDesignCompact } = useDesignLayout()
  const { isCompact: viewportCompact, isLandscapeViewport } = useSessionLayout()
  const paymentNarrowLandscape = viewportCompact && isLandscapeViewport
  const paymentTight = paymentDesignCompact || paymentNarrowLandscape
  /** Short foldables / landscape: cap QR box and scroll the glass panel so nothing clips */
  const paymentGlass = paymentNarrowLandscape
    ? 'custom-scrollbar mx-auto flex max-h-[min(92dvh,calc(100dvh-5rem))] w-full max-w-md min-h-0 flex-col items-center gap-2 overflow-y-auto px-4 py-3'
    : paymentTight
      ? 'custom-scrollbar mx-auto flex max-h-[min(92dvh,calc(100dvh-5rem))] w-full max-w-xl min-h-0 flex-col items-center gap-3 overflow-y-auto px-6 py-4'
      : 'custom-scrollbar mx-auto flex max-h-[min(92dvh,calc(100dvh-5rem))] w-full max-w-2xl min-h-0 flex-col items-center gap-4 overflow-y-auto p-12'
  const paymentQrBox = paymentNarrowLandscape
    ? 'h-[min(9rem,32vh)] w-[min(9rem,32vh)] min-h-0 shrink-0'
    : paymentTight
      ? 'h-[min(15rem,42vh)] w-[min(15rem,42vh)] min-h-0 shrink-0'
      : 'h-[min(25rem,48vh)] w-[min(25rem,48vh)] min-h-0 shrink-0'
  const paymentRedirectBox = paymentNarrowLandscape
    ? 'h-[min(22rem,75vh)] w-full max-w-sm min-h-0 shrink-0'
    : paymentTight
      ? 'h-[min(30rem,70vh)] w-full max-w-md min-h-0 shrink-0'
      : 'h-[min(36rem,70vh)] w-full max-w-md min-h-0 shrink-0'
  const paymentTitle = paymentNarrowLandscape ? 'text-lg' : paymentTight ? 'text-2xl' : 'text-4xl'
  const paymentHint = paymentNarrowLandscape
    ? 'text-xs text-center text-gray-600'
    : paymentTight
      ? 'text-sm text-center text-gray-600'
      : 'text-base text-center text-gray-600'
  const paymentRemainingTimeClass = paymentNarrowLandscape
    ? 'text-center font-mono tabular-nums text-base text-carbon'
    : paymentTight
      ? 'text-center font-mono tabular-nums text-xl text-carbon'
      : 'text-center font-mono tabular-nums text-3xl text-carbon'
  const paymentBtnCompact = paymentNarrowLandscape
    ? '!px-3 !py-1.5 !text-xs'
    : paymentTight
      ? '!px-4 !py-2 !text-sm'
      : '!px-6 !py-2.5 !text-base'
  const { config: globalConfig, getPageSettings } = useConfigStore()
  const { primaryColor } = globalConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }
  const config = globalConfig?.pages.payment
  const welcomeSettings = getPageSettings('welcome')
  const isMounted = useRef(true)

  /** Display currency (write-time stamp from the backend). */
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)
  /** True when the project's tier renders QRIS without a custom gateway. */
  const tier = globalConfig?.paymentSetupTier
  const hasCustomGateway = Boolean(globalConfig?.hasCustomGateway)
  // Voucher-only iff the tier has no built-in QRIS rail AND no custom gateway
  // is wired. `global_hidden` (which MXN maps to) is NOT unconditionally
  // voucher-only: once the operator wires Mercado Pago, online payment is
  // available and we must mint a real charge instead of showing the voucher
  // UI. Mirrors the authoritative gate in the studio's `/api/payments`
  // (`isVoucherOnlyCheckout`) — keep the two in sync.
  const isVoucherOnlyRegion =
    (tier === 'global_hidden' || tier === 'localized_in_progress') &&
    !hasCustomGateway

  // Helper to safely update state/logs only when component is mounted
  const safeUpdate = (callback: () => void): void => {
    if (isMounted.current) {
      callback()
    }
  }

  const [remainingTime, setRemainingTime] = useState<string>(COUNTDOWN_PLACEHOLDER)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [snapToken, setSnapToken] = useState<string | null>(null)
  const [clientKey, setClientKey] = useState<string | null>(null)
  const [paymentType, setPaymentType] = useState<PaymentType>('core')
  const [activeProvider, setActiveProvider] = useState<PaymentProvider>('midtrans')
  /**
   * How guest-facing copy names the rail. "QRIS" only when the guest is really
   * looking at a QRIS code, which is also exactly when the QRIS logo below is
   * shown. Razorpay's code is a UPI QR, so it says "UPI". Everywhere else
   * (ToyyibPay's DuitNow QR, an operator's own gateway outside the QRIS
   * footprint) it is just "QR".
   */
  const guestRail = guestRailFor(activeProvider, isVoucherOnlyRegion)
  const guestRailLabel =
    guestRail === 'qris'
      ? t('payment.rail.qris', 'QRIS')
      : guestRail === 'upi'
        ? t('payment.rail.upi', 'UPI')
        : t('payment.rail.online', 'QR')
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null)
  const [billWindowOpen, setBillWindowOpen] = useState(false)
  const [billWindowError, setBillWindowError] = useState<string | null>(null)
  const billWindowOpenedForUrlRef = useRef<string | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  // Flips to `true` as soon as the /api/payments call resolves (success or
  // failure). Used to hide the QRIS-themed payment panel during the initial
  // request so non-QRIS rails (e.g. ToyyibPay → redirect popup) never flash
  // the QRIS logo / countdown / QR box on the way to the gateway's hosted
  // page. QRIS rails simply see the panel appear once the QR is ready,
  // which actually feels snappier than today's "empty QR slot then QR".
  const [paymentInitialized, setPaymentInitialized] = useState(false)
  const [showSkipGuide, setShowSkipGuide] = useState(false)
  const [snapPaymentOpened, setSnapPaymentOpened] = useState(false)
  const [showCodeEntry, setShowCodeEntry] = useState(false)
  // Customer voucher entry (NOT the operator PIN). Wired in-place on the
  // Payment page so the event-template flow (Welcome → Payment, with
  // `startWithPayment: true`) works without a Checkout step to fall back
  // to. Validated via `/api/promos/apply`, same as Checkout's promo input.
  const [showVoucherInput, setShowVoucherInput] = useState(false)
  /**
   * Card on the operator's own terminal. The guest taps the card button, the booth
   * HOLDS the session (the QRIS charge keeps running underneath — if they scan it
   * after all, it still settles), staff run the card on the EDC and confirm on a
   * PIN-gated step, keying the approval code off the receipt. Settles like the
   * cash voucher: status 'settlement' plus the method, the terminal and who
   * confirmed. Rendered only when the booth has a terminal configured; QRIS at
   * 0% MDR is never displaced by a ~3% rail.
   */
  const [cardHold, setCardHold] = useState(false)
  /**
   * The other card mode: a CONNECTED terminal (`cardTerminal.mode ===
   * 'connected'`). Studio pushes the amount to a Mercado Pago Point terminal
   * and the booth follows that order instead of a QR, so nothing is keyed by
   * hand. Same button, same panel, same session fields as the manual flow
   * above; only `collectedBy` differs, because this money did go through a
   * gateway.
   *
   * The refs shadow the state because the countdown's completion handler and
   * the poll are bound once and would otherwise read the first render's copy.
   */
  const [pointStatus, setPointStatus] = useState<PointOrderStatus | null>(null)
  const [pointWindowOver, setPointWindowOver] = useState(false)
  const [terminalBusy, setTerminalBusy] = useState(false)
  const pointStatusRef = useRef<PointOrderStatus | null>(null)
  const pointWindowOverRef = useRef(false)
  const pointReferenceRef = useRef<string | null>(null)
  const pointCancelInFlightRef = useRef(false)
  const paymentTypeRef = useRef<PaymentType>('core')
  const setPointStatusLive = (status: PointOrderStatus | null): void => {
    pointStatusRef.current = status
    setPointStatus(status)
  }
  const setPointWindowOverLive = (over: boolean): void => {
    pointWindowOverRef.current = over
    setPointWindowOver(over)
  }
  /**
   * An order the terminal may be holding: the guest could have their card in
   * the reader, so nothing may replace it or reset the session under them.
   */
  const terminalOrderOpen = (): boolean =>
    paymentTypeRef.current === 'terminal' &&
    useSessionStateStore.getState().transaction?.status !== 'settlement' &&
    pointCountdownAction(pointStatusRef.current) !== 'expire'
  const [showCardPin, setShowCardPin] = useState(false)
  const [showCardCode, setShowCardCode] = useState(false)
  const [cardCodeError, setCardCodeError] = useState('')
  const [voucherError, setVoucherError] = useState('')
  const [isVoucherLoading, setIsVoucherLoading] = useState(false)
  const [isOnline, setIsOnline] = useState(true)
  const [networkError, setNetworkError] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const countdownPausedAtRef = useRef<number | null>(null)
  const countdownPausedSecondsRef = useRef<number>(0)
  const isOnlineRef = useRef<boolean>(true)
  const { password, cardTerminal } = useSettingsStore()
  // A connected terminal needs both halves to be real: a terminal paired with
  // THIS booth, and the integration that drives it (a Nayax reader on its own,
  // a Point terminal through the project's Mercado Pago gateway). Without them
  // the button would promise a rail this booth cannot reach, so it stays
  // hidden rather than failing after the guest presses it.
  const connectedCard = isConnectedCardReady(cardTerminal, activeProvider)
  const nayaxTerminal =
    connectedCard && connectedTerminalProvider(cardTerminal) === 'nayax'
  const cardEnabled = cardTerminal.mode === 'manual' || connectedCard
  const {
    id,
    transaction,
    setSessionState,
    userPreferences,
    mode,
    userId,
    logActivity
  } = useSessionStateStore()
  const sessionReset = useSessionReset()
  const navigate = useNavigate()
  const { projectId } = useParams()

  // Initialize payment countdown
  const paymentCountdown = useCountdown(0) // We'll set the actual seconds when payment is initiated

  // Initialize auto-reset countdown (15 seconds)
  const autoResetCountdown = useCountdown(AUTO_RESET_SECONDS)

  // Set up auto-reset countdown callbacks
  useEffect(() => {
    autoResetCountdown.setOnTick(() => {
      setRemainingTime(autoResetCountdown.getFormattedRemainingSeconds())
    })

    autoResetCountdown.setOnComplete(() => {
      // Safety: never dump a session that settled in the race between expiry and payment
      // confirmation. If it somehow paid, let the normal success flow advance instead.
      if (useSessionStateStore.getState().transaction?.status === 'settlement') {
        return
      }
      sessionReset('payment_expired_autoreset')
    })
  }, [])

  // Watch for payment expiration to start auto-reset countdown
  useEffect(() => {
    if (isExpired) {
      autoResetCountdown.start()
      return (): void => {
        autoResetCountdown.stop()
      }
    }
    return
  }, [isExpired])

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      const remainingMs = expiryTimestamp - serverTimestamp
      return Math.max(0, Math.floor(remainingMs / 1000))
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return 0
    }
  }

  const handleCountdownEnd = async (): Promise<void> => {
    if (!isMounted.current) {
      return
    }
    // Check if the session for which this timer was started is still the active session.
    if (id !== useSessionStateStore.getState().id) {
      logActivity('Ignoring timeout for a previous session.')
      return
    }

    // Only trigger expiration if online - if offline, pause countdown instead
    if (!isOnline) {
      safeUpdate(() => {
        paymentCountdown.stop()
        const remainingSeconds = paymentCountdown.getRemainingSeconds()
        countdownPausedSecondsRef.current = remainingSeconds
        countdownPausedAtRef.current = Date.now()
        setNetworkError(true)
        logActivity('Payment countdown paused - network disconnected', {
          orderId: transaction.orderId,
          remainingSeconds
        })
      })
      return
    }

    await checkPaymentStatus()
    // That last check may have found the payment. Writing 'expire' over a
    // settlement is how a guest who paid in the final seconds ended up with a
    // session the booth threw away.
    if (useSessionStateStore.getState().transaction?.status === 'settlement') {
      logActivity('Payment settled on the final check; not expiring.', {
        orderId: useSessionStateStore.getState().transaction?.orderId
      })
      return
    }
    // A terminal order outlives the booth's window on purpose: Mercado Pago
    // keeps it alive a couple of minutes longer so a guest who is mid-payment
    // can finish. Expiring here would throw away a session they are paying for.
    if (paymentTypeRef.current === 'terminal') {
      const action = pointCountdownAction(pointStatusRef.current)
      if (action === 'none') return
      if (action === 'wait' || action === 'cancel') {
        const cancelled =
          action === 'cancel' ? canLeaveAfterCancel(await cancelTerminalOrder()) : false
        if (!cancelled) {
          safeUpdate(() => {
            setPointWindowOverLive(true)
            setRemainingTime('00:00')
            logActivity('Payment window over while the terminal holds the order', {
              orderId: useSessionStateStore.getState().transaction?.orderId,
              pointStatus: pointStatusRef.current
            })
          })
          return
        }
      }
    }
    safeUpdate(() => {
      setIsCheckingPayment(false)
      setRemainingTime('00:00')
      setIsExpired(true)
      // The card panel covers the expired screen, and there is nothing left on
      // the terminal to talk about. (The manual hold keeps its panel: staff may
      // still be walking over with the card.)
      if (paymentTypeRef.current === 'terminal') setCardHold(false)
      logActivity('Payment timed out.', {
        orderId: transaction.orderId,
        paymentType
      })
      setSessionState({
        transaction: {
          status: 'expire'
        }
      })
    })
  }

  // Set up payment countdown callbacks
  useEffect(() => {
    paymentCountdown.setOnTick(() => {
      // Only update countdown display if online
      if (isOnline) {
        setRemainingTime(paymentCountdown.getFormattedRemainingSeconds())
      }
    })

    paymentCountdown.setOnComplete(handleCountdownEnd)
  }, [isOnline])

  // Resume from an existing transaction: bind the countdown (or the expired
  // state) immediately from session state instead of waiting for the payment
  // API round-trip. Runs once on mount; the init/polling paths keep overriding
  // with fresher server data as it arrives.
  const resumedFromTransactionRef = useRef(false)
  useEffect(() => {
    if (resumedFromTransactionRef.current) return
    resumedFromTransactionRef.current = true
    if (transaction.status === 'expire') {
      setIsExpired(true)
      return
    }
    if (
      transaction.status === 'pending' &&
      transaction.expiryTime &&
      transaction.serverTime
    ) {
      const remaining = calculateRemainingTime(transaction.expiryTime, transaction.serverTime)
      if (remaining <= 0) {
        setIsExpired(true)
      } else {
        paymentCountdown.stop()
        paymentCountdown.setRemainingSeconds(remaining)
        paymentCountdown.start()
        setRemainingTime(paymentCountdown.getFormattedRemainingSeconds())
      }
    }
  }, [])

  const getDefaultPrice = (): number => {
    const packages = globalConfig?.pages.checkout?.settings?.packages || []
    // `Array.prototype.filter` doesn't narrow `(Package | undefined)[]` to
    // `Package[]` on its own — use an explicit type predicate so the
    // reducer below sees a non-null `pkg` and TS stays happy.
    const isPackage = (pkg: Package | undefined): pkg is Package => pkg !== undefined
    let selectedPackages: Package[] = []

    if (mode === 'record') {
      selectedPackages = [packages.find((pkg) => pkg.type === 'video')].filter(isPackage)
    } else {
      // `mode === 'capture'` OR undefined: the event-template flow
      // (Welcome → Payment with `startWithPayment: true`) reaches this
      // page before any Capture/Record step has set `mode`, so an
      // undefined mode used to fall through both arms and return 0 —
      // which displayed as "₹ 0.00" in voucher-only regions. Treat
      // undefined as capture (the universal default) so the
      // first-impression price is meaningful.
      selectedPackages = [
        packages.find((pkg) => pkg.type === 'print'),
        packages.find((pkg) => pkg.type === 'gif'),
        packages.find((pkg) => pkg.type === 'recording')
      ].filter(isPackage)
    }

    return selectedPackages.reduce((subtotal, pkg) => subtotal + (pkg.price || 0), 0)
  }

  const cleanupPayment = useCallback(async (): Promise<void> => {
    if (transaction.orderId && transaction.status !== 'settlement') {
      // Same DELETE as before, through the helper so a card-terminal order
      // carries its Mercado Pago reference and its answer is typed. Errors are
      // swallowed there: this runs while the page is being torn down.
      const outcome = await MidtransUtils.cancelPayment(transaction.orderId, {
        reference: pointReferenceRef.current
      })
      // A terminal order this could not end (a guest is paying on it, or Studio
      // was unreachable) stays remembered for the leftover sweep in App.
      if (canLeaveAfterCancel(outcome)) forgetOpenTerminalOrder(transaction.orderId)
    }
  }, [transaction.orderId, transaction.status])

  useEffect(() => {
    // Wait for state updates to propagate (especially from Checkout page)
    const initializePayment = async (): Promise<void> => {
      // Use microtask to let state updates from Checkout propagate
      await Promise.resolve()

      // Get latest state inside async function to avoid stale closure
      const latestState = useSessionStateStore.getState()
      const latestTransaction = latestState.transaction

      if (latestTransaction.amount !== undefined && latestTransaction.amount !== null) {
        console.log(
          `[PAYMENT] Amount already set from Checkout: Rp${latestTransaction.amount} (discount: ${latestTransaction.discount || 0})`
        )
        console.log(`[PAYMENT] Transaction data from Checkout:`, {
          amount: latestTransaction.amount,
          discount: latestTransaction.discount,
          status: latestTransaction.status,
          selectedPackages: latestTransaction.selectedPackages?.length || 0
        })
        stateUploader.uploadSessionState()
        return
      }

      console.warn('[PAYMENT] Amount not set, calculating fallback amount')
      let calculatedAmount: number

      // If selectedPackages exist, calculate from them (accounts for user selections)
      if (latestTransaction.selectedPackages && latestTransaction.selectedPackages.length > 0) {
        const subtotal = latestTransaction.selectedPackages.reduce(
          (sum, pkg) => sum + roundMoney(pkg.price || 0, projectCurrency),
          0
        )
        const discount = roundMoney(latestTransaction.discount || 0, projectCurrency)
        calculatedAmount = roundMoney(Math.max(0, subtotal - discount), projectCurrency)
      } else {
        // Fallback to default price if no packages selected
        calculatedAmount = getDefaultPrice()
      }

      // Only update if amount was truly missing
      console.log(`[PAYMENT] Fallback: Setting calculated amount: Rp${calculatedAmount}`)
      setSessionState({
        transaction: {
          amount: calculatedAmount
        }
      })
      console.log(`[PAYMENT] Transaction amount set in state:`, { amount: calculatedAmount })

      // Wait another microtask for state update to propagate before uploading
      await Promise.resolve()

      // Upload session state after ensuring correct amount
      stateUploader.uploadSessionState()
    }

    initializePayment().catch((error) => {
      console.error('Error initializing payment:', error)
      // Fallback: try to upload anyway
      stateUploader.uploadSessionState()
    })

    return (): void => {
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      autoResetCountdown.stop()
      cleanupPayment()
    }
  }, [])

  // Handle orderId changes
  useEffect(() => {
    // When component unmounts or orderId changes, cleanup previous payment if needed
    return (): void => {
      if (transaction.orderId && transaction.status !== 'settlement') {
        cleanupPayment()
      }
    }
  }, [transaction.orderId, cleanupPayment])

  // useEffect(() => {
  //   // You can also change below url value to any script url you wish to load,
  //   const midtransScriptUrl = 'https://app.midtrans.com/snap/snap.js'

  //   const scriptTag = document.createElement('script')
  //   scriptTag.src = midtransScriptUrl

  //   document.body.appendChild(scriptTag)

  //   return (): void => {
  //     document.body.removeChild(scriptTag)
  //   }
  // }, [])

  const handleSnapPaymentSuccess = async (result: unknown): Promise<void> => {
    console.log('Snap payment successful:', result)
    setIsCheckingPayment(false)
    cogoToast.success(t('payment.successMessage', 'Payment successful!'))
    logActivity('Payment successful via Snap.', {
      orderId: transaction.orderId,
      paymentType: 'snap',
      snapResult: result
    })
    setSessionState({
      transaction: {
        status: 'settlement'
      }
    })
    stateUploader.uploadSessionState()
    const filterSettings = globalConfig?.pages.filter?.settings
    const applyAfterCapture = filterSettings?.applyAfterCapture || false
    // Camera selection already happened before checkout (Welcome routes every
    // secondary-camera session through /camera first), so there is nothing to
    // divert to here — sending the guest back would be a second camera prompt.
    const nextPage = welcomeSettings?.startWithPayment
      ? applyAfterCapture
        ? 'frame'
        : 'filter'
      : 'capture'
    navigate(`/${projectId}/${nextPage}`)
  }

  const handleSnapPaymentError = (result: unknown): void => {
    console.error('Snap payment error:', result)
    if (isOnlineRef.current) {
      cogoToast.error(t('payment.snapError', 'Payment failed. Please try again.'))
    }
    logActivity('Payment failed via Snap.', {
      orderId: transaction.orderId,
      paymentType: 'snap',
      error: result,
      level: 'error'
    })
    // P1-3: use sessionReset so the failed transaction, gallery URLs and
    // session logs are cleaned up before returning to Welcome — otherwise
    // the next customer would inherit the previous orderId/transaction.
    sessionReset('snap_payment_error')
  }

  const handleSnapPaymentClose = (): void => {
    setSnapPaymentOpened(false)
    // Continue checking payment status in case user completed payment
    setIsCheckingPayment(true)
  }

  // Open ToyyibPay bill page in a real child BrowserWindow (popup). Webview
  // embedding is unreliable across Electron versions, and toyyibPay refuses
  // iframe embedding via X-Frame-Options. The popup uses real Chromium, so
  // it renders identically to opening the URL in a regular browser. Polling
  // we already do will detect payment completion automatically.
  const openBillWindow = useCallback(
    async (urlOverride?: string): Promise<void> => {
      const url = urlOverride || redirectUrl
      if (!url) return
      // Bridge helper returns status='unavailable' when running outside an
      // Electron shell with the bill-window IPC wired up (e.g. Tauri/web).
      if (!hasToyyibpayBillApi()) {
        setBillWindowError(
          t('payment.popupUnavailable', 'Payment window is unavailable in this build.')
        )
        return
      }

      try {
        setBillWindowError(null)
        setBillWindowOpen(true)
        billWindowOpenedForUrlRef.current = url
        const result = await openToyyibpayBill(url)
        // A re-charge replaced this bill's window with the new bill's while it
        // was open. The new window's own request reports on it; this answer is
        // about a dead bill and must not mark the payment window as closed.
        if (billWindowOpenedForUrlRef.current !== url) return
        // Window has closed by the time the promise resolves (or it was a
        // duplicate request that was just refocused, or the bridge said the
        // host doesn't expose the IPC).
        if (result.status === 'reused') {
          // Already had a window open; treat as still open.
          return
        }
        setBillWindowOpen(false)
        if (result.status === 'unavailable') {
          setBillWindowError(
            t('payment.popupUnavailable', 'Payment window is unavailable in this build.')
          )
          logActivity('ToyyibPay bill window unavailable', {
            url,
            level: 'error',
            reason: result.reason
          })
        } else if (result.status === 'failed') {
          setBillWindowError(
            t(
              'payment.popupFailed',
              'Could not open the payment window. Please try again.'
            )
          )
          logActivity('ToyyibPay bill window failed to load', {
            url,
            level: 'error',
            reason: result.reason,
            finalUrl: result.finalUrl
          })
        } else if (result.status === 'completed') {
          logActivity('ToyyibPay bill window completed', {
            url,
            finalUrl: result.finalUrl
          })
          // Polling already running will pick up the settlement; nothing to do.
        } else {
          logActivity('ToyyibPay bill window closed by user', {
            url,
            finalUrl: result.finalUrl
          })
        }
      } catch (error) {
        setBillWindowOpen(false)
        const message = error instanceof Error ? error.message : 'unknown'
        setBillWindowError(
          t('payment.popupFailed', 'Could not open the payment window. Please try again.')
        )
        console.error('[Payment] openToyyibpayBill failed', error)
        logActivity('ToyyibPay bill window error', {
          url,
          error: message,
          level: 'error'
        })
      }
    },
    [redirectUrl, t]
  )

  const openSnapPayment = async (): Promise<void> => {
    if (!snapToken || !clientKey || snapPaymentOpened) return

    try {
      window.snap.pay(snapToken, {
        onSuccess: (result: unknown) => {
          console.log('Snap payment success:', result)
          handleSnapPaymentSuccess?.(result)
        },
        onError: (result: unknown) => {
          console.error('Snap payment error:', result)
          handleSnapPaymentError?.(result)
        },
        onClose: () => {
          console.log('Snap payment popup closed')
          handleSnapPaymentClose?.()
        }
      })
    } catch (error) {
      console.error('Error opening Snap payment:', error)
      throw error
    }
  }

  /**
   * Create the charge for this session. With `terminalId`, Studio pushes the
   * amount to that card terminal instead of minting a QR and re-stamps the
   * session with the new order, so the poll already running follows the
   * terminal from here.
   */
  const initiatePayment = async (
    options: { terminalId?: string; terminalProvider?: 'nayax' } = {}
  ): Promise<void> => {
    // Already settled (e.g. cash voucher): skip payment and navigate to next page
    if (transaction.status === 'settlement') {
      // No /api/payments call happens, so flip the loading gate
      // immediately to release the rendering guard below before we
      // navigate away.
      setPaymentInitialized(true)
      const filterSettings = globalConfig?.pages.filter?.settings
      const applyAfterCapture = filterSettings?.applyAfterCapture || false
      const nextPage = welcomeSettings?.startWithPayment
        ? applyAfterCapture
          ? 'frame'
          : 'filter'
        : 'capture'
      navigate(`/${projectId}/${nextPage}`)
      return
    }

    // Voucher-only region: never call /api/payments. We render an
    // in-place "Enter Voucher Code" button on the Payment page itself
    // (see `renderPaymentInterface` below) instead of redirecting back
    // to Checkout — the event-template flow goes Welcome → Payment
    // directly (`startWithPayment: true`) and never sees a Checkout
    // step, so a redirect would bounce the customer onto an unfamiliar
    // page. The operator-PIN modal stays reserved for the offline-mode
    // bypass and must never gate the voucher-only flow.
    if (isVoucherOnlyRegion) {
      // Surface the voucher-entry UI right away (no payment call to wait for).
      setPaymentInitialized(true)
      logActivity('Payment voucher-only region — awaiting customer voucher input', {
        paymentSetupTier: tier
      })
      return
    }

    // Check network status before initiating payment
    if (!isOnline) {
      setNetworkError(true)
      // Surface the offline UI (which lives inside the panel) — no
      // network call will fire to flip the gate from the success path.
      setPaymentInitialized(true)
      logActivity('Payment initiation blocked - no internet connection', {
        isOnline: false
      })
      return
    }

    // Reset network error state when attempting to initiate
    setNetworkError(false)

    try {
      // `grossAmount` MUST be in the project's native currency — the backend
      // does the FX conversion to settlement currency (Midtrans cross-border
      // QRIS → IDR, ToyyibPay → MYR). On the first call `transaction.amount`
      // is still the native value (set from Checkout / `getDefaultPrice`).
      // On retry it has already been overwritten with the SETTLEMENT amount
      // (e.g. IDR 67014 for an MYR project) by the session writer below, so
      // reading it raw would re-feed an IDR figure as MYR and the backend
      // would multiply it by the FX rate again — a 3500× overcharge.
      // Always prefer `nativeAmount` (preserved separately) when present.
      //
      // Use `||`, NOT `??`: `transaction.amount` is initialised to literal `0`
      // in the session store, and `??` only falls through on null/undefined.
      // In the `startWithPayment: true` flow (Welcome → Payment, no Checkout
      // step) nothing has written a price yet, so the chain short-circuited on
      // that `0` and never reached `getDefaultPrice()`. We then POSTed
      // `grossAmount: 0`, Midtrans rejected the zero-amount charge, and the
      // booth showed a bare "Payment initiation failed:" with no QR.
      const nativeGrossAmount =
        transaction.nativeAmount || transaction.amount || getDefaultPrice()
      const data = await MidtransUtils.initiatePayment(
        nativeGrossAmount,
        id || '',
        config?.settings.paymentTimeout || 120,
        projectId || '',
        userPreferences.filterId || '',
        userPreferences.frameId || '',
        transaction.discount || 0,
        false,
        // Provider hint only: Studio resolves the operator's real gateway from
        // the project's wallet, exactly as it does without these two.
        'midtrans',
        false,
        { terminalId: options.terminalId, terminalProvider: options.terminalProvider }
      )

      if (data.order_id) {
        // Normalise the gateway the backend resolved this charge to. Mercado
        // Pago (Checkout Pro QR), Tap (hosted Checkout QR) and Razorpay (UPI
        // QR) all come back as `payment_type: 'core'` like Midtrans QRIS, so
        // they render through the same on-screen QR path.
        const resolvedProvider: PaymentProvider = normalizePaymentProvider(data.provider)
        setPaymentType(data.payment_type)
        paymentTypeRef.current = data.payment_type
        setActiveProvider(resolvedProvider)
        setRedirectUrl(data.redirect_url || null)

        // Calculate initial remaining time
        const initialRemainingSeconds = calculateRemainingTime(data.expiry_time, data.server_time)

        if (initialRemainingSeconds <= 0) {
          setIsExpired(true)
          return
        }

        // Session contract: `amount` + `currency` track the GATEWAY
        // SETTLEMENT (Midtrans → IDR, ToyyibPay → MYR), NOT the project's
        // display currency. `data.amount` / `data.charge_currency` come
        // back from /api/payments with the actual gateway charge. We
        // preserve the native price the customer saw on
        // `nativeAmount` / `nativeCurrency` so dashboards can surface
        // "RM 14.50 (charged as Rp 49,300)" reconciliation, and stash
        // `fxRate` for finance to recompute from. Same-currency rails
        // (IDR project on Midtrans) get matching native + settlement.
        const settlementAmount =
          typeof data.amount === 'number' ? data.amount : Number(transaction.amount) || 0
        // Settlement currency is a free-string from the payment API
        // (gateway-supplied); the kiosk's `LocalCurrency` union covers
        // every code the backend can return today, so coerce via
        // `normalizeCurrency` to keep the type narrow.
        const settlementCurrency = normalizeCurrency(
          data.charge_currency || data.currency || projectCurrency
        )
        // Use the same `nativeGrossAmount` we just sent to the backend, not
        // `transaction.amount` — the latter has already been overwritten with
        // the prior call's SETTLEMENT amount on retry, so persisting it as
        // `nativeAmount` would corrupt the cross-currency reconciliation
        // (dashboards would see e.g. "RM 67014" instead of "RM 19").
        const nativeAmount = Number(nativeGrossAmount)
        const sessionData = {
          transaction: {
            orderId: data.order_id,
            walletId: data.wallet_id || 'dreambooth',
            provider: resolvedProvider,
            qrUrl: '',
            expiryTime: data.expiry_time,
            serverTime: data.server_time,
            status: 'pending' as TransactionStatus,
            amount: settlementAmount,
            currency: settlementCurrency,
            nativeAmount,
            nativeCurrency: projectCurrency,
            ...(typeof data.fx_rate === 'number' ? { fxRate: data.fx_rate } : {}),
            discount: transaction.discount || 0,
            paymentType: data.payment_type
          }
        }
        console.log(
          `[PAYMENT] Payment initiated - transaction amount: Rp${transaction.amount} (discount: ${transaction.discount || 0})`
        )
        logActivity('Payment initiated.', {
          amount: transaction.amount,
          orderId: data.order_id,
          expiryTime: data.expiry_time,
          serverTime: data.server_time,
          remainingSeconds: initialRemainingSeconds,
          paymentType: data.payment_type
        })

        if (data.payment_type === 'core' && data.qr_url) {
          // Core API with QR code
          const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')
          setQrUrl(sanitizedQrUrl)
          sessionData.transaction.qrUrl = sanitizedQrUrl
        } else if (data.payment_type === 'snap' && data.snap_token) {
          // Snap payment
          setSnapToken(data.snap_token)
          setClientKey(data.client_key || '')
        } else if (data.payment_type === 'terminal') {
          // The amount is on the terminal now. The card panel stays up and
          // follows the order; `gateway_reference` is the Mercado Pago order
          // id, echoed back on every status and cancel call for the moments
          // before this session's first upload lands.
          pointReferenceRef.current = data.gateway_reference || null
          setPointStatusLive('created')
          setPointWindowOverLive(false)
          setCardHold(true)
        }

        if (data.payment_type !== 'terminal') {
          pointReferenceRef.current = null
          setPointStatusLive(null)
          setPointWindowOverLive(false)
          if (options.terminalId) {
            // Asked for the terminal, got a QR: the project's gateway is not
            // the one that drives it. Show the code rather than a dead panel.
            logActivity('Card terminal unavailable, showing the payment code', {
              orderId: data.order_id,
              paymentType: data.payment_type
            })
            cogoToast.error(
              t(
                'payment.card.terminalUnavailable',
                'The card terminal is not available. Please pay with the code on screen.'
              )
            )
            setCardHold(false)
          }
        }

        setSessionState(sessionData)
        if (data.payment_type === 'terminal') {
          // Remembered outside the store, which a reload or a power cut wipes
          // while the terminal keeps showing the amount. Written after the store
          // holds the order, so the leftover sweep already treats it as live.
          rememberOpenTerminalOrder({
            orderId: data.order_id,
            reference: data.gateway_reference || null,
            sessionId: id,
            expiresAt:
              Date.now() +
              Math.max(0, initialRemainingSeconds) * 1000 +
              POINT_ORDER_EXPIRY_MARGIN_MS
          })
        }
        setIsExpired(false)

        // Stop any existing countdown and start new one
        paymentCountdown.stop()
        paymentCountdown.setRemainingSeconds(initialRemainingSeconds)
        paymentCountdown.start()

        // Reset countdown pause tracking
        countdownPausedAtRef.current = null
        countdownPausedSecondsRef.current = 0

        setIsCheckingPayment(true)
        isMounted.current = true
      }
    } catch (e: unknown) {
      // Backend declined QRIS for this region — render the in-place
      // voucher input on the Payment page (no redirect; see the
      // pre-`/api/payments` guard above for the same contract).
      if (e instanceof VoucherOnlyRegionError) {
        logActivity('Payment switched to voucher-only (backend 409)', {
          paymentSetupTier: e.paymentSetupTier
        })
        return
      }
      const error = e as Error
      const isNetworkError =
        !isOnline || error.message.includes('fetch') || error.message.includes('network')

      if (isNetworkError) {
        setNetworkError(true)
        logActivity('Payment initiation failed - network error', {
          error: error.message,
          isOnline,
          level: 'error'
        })
      } else {
        logActivity(`Payment initiation failed: ${error.message}`, {
          error: error.message,
          level: 'error'
        })
      }
      console.error('Error initiating payment:', e)
      // Only show error if online and not in offline mode
      if (isOnlineRef.current) {
        cogoToast.error(<span>{error.message}</span>)
      }
    } finally {
      // `paymentInitialized` gates the QRIS-themed panel chrome. Flip it
      // regardless of branch — success leaves the panel to render either
      // the QR (Midtrans/core) or a transparent spinner (ToyyibPay/redirect,
      // gated separately by `useStraightRedirectUI`); failures still need
      // the panel so the customer can see the error / timer / retry path.
      setPaymentInitialized(true)
    }
  }

  // Check internet connectivity for offline mode
  useEffect(() => {
    if (typeof window.api !== 'undefined' && window.api.getInternetStatus) {
      window.api.getInternetStatus().then((status: boolean) => {
        setIsOnline(status)
        isOnlineRef.current = status
      })

      const unsubscribe = window.api.onInternetStatusChanged((status: boolean) => {
        setIsOnline(status)
        isOnlineRef.current = status
      })

      return unsubscribe
    }
    return (): void => {}
  }, [])

  // Mount and unmount only. This used to be keyed on `isOnline`, which made
  // every connectivity blip run the teardown AND create a second charge: the
  // guest could be looking at the first checkout on their phone while the booth
  // watched only the second, so paying stranded them. It also set
  // `isMounted.current = false` on a blip, which silently disabled the poll and
  // the reconnect re-check for the rest of the session.
  const cleanupPaymentRef = useRef(cleanupPayment)
  cleanupPaymentRef.current = cleanupPayment
  useEffect(() => {
    if (isOnline) {
      initiatePayment()
    }

    return (): void => {
      // Removed logActivity to keep activity logs clean - no payment-related logs outside payment page
      isMounted.current = false
      setIsCheckingPayment(false)
      paymentCountdown.stop()
      autoResetCountdown.stop()
      // Through a ref: a mount-only effect would otherwise capture the very
      // first render's cleanup, which has no orderId yet and cancels nothing.
      cleanupPaymentRef.current()
      // Best-effort close of the toyyibPay popup when leaving the page;
      // bridge helper no-ops outside Electron.
      closeToyyibpayBill()
    }
  }, [])

  // Back online: resume the charge we already have, and only mint a new one
  // when there is nothing left to resume (see `shouldCreateNewCharge`).
  //
  // The first run is the mount, which the effect above already handled — a
  // fresh page needs its own charge either way, and re-using an order left in
  // the store would show a countdown with no QR behind it.
  const initialOnlineHandledRef = useRef(false)
  useEffect(() => {
    if (!initialOnlineHandledRef.current) {
      initialOnlineHandledRef.current = true
      return
    }
    if (!isOnline) return
    const live = useSessionStateStore.getState().transaction
    if (
      !shouldCreateNewCharge({
        orderId: live?.orderId,
        status: live?.status,
        terminalOrderOpen: terminalOrderOpen(),
        pausedSeconds: countdownPausedSecondsRef.current,
        pausedAtMs: countdownPausedAtRef.current,
        nowMs: Date.now()
      })
    ) {
      logActivity('Back online with a live charge; resuming instead of re-charging', {
        orderId: live?.orderId
      })
      return
    }
    initiatePayment()
  }, [isOnline])

  // Auto-open the ToyyibPay bill popup as soon as a redirect URL is
  // available. Only opens once per URL so re-renders don't spawn duplicates.
  useEffect(() => {
    if (paymentType !== 'redirect') return
    if (!redirectUrl) return
    if (isExpired) return
    if (transaction.status === 'settlement') return
    if (billWindowOpenedForUrlRef.current === redirectUrl) return
    openBillWindow(redirectUrl)
  }, [paymentType, redirectUrl, isExpired, transaction.status, openBillWindow])

  // If the payment completes (settlement) or expires while the popup is
  // still open, close it so the user isn't left staring at a dead bill page.
  useEffect(() => {
    const shouldClose = isExpired || transaction.status === 'settlement'
    if (!shouldClose) return
    // Bridge helper is a safe no-op outside Electron.
    closeToyyibpayBill()
  }, [isExpired, transaction.status])

  // Handle network status transitions
  useEffect(() => {
    if (!isOnline) {
      // Network went offline - pause countdown and polling
      if (paymentCountdown.getRemainingSeconds() > 0) {
        const remainingSeconds = paymentCountdown.getRemainingSeconds()
        paymentCountdown.stop()
        countdownPausedSecondsRef.current = remainingSeconds
        countdownPausedAtRef.current = Date.now()
        setNetworkError(true)
        setIsCheckingPayment(false)
        logActivity('Network disconnected during payment', {
          orderId: transaction.orderId,
          remainingSeconds
        })
      }
    } else {
      // Network came back online - resume countdown and polling if payment was in progress
      if (countdownPausedAtRef.current !== null && countdownPausedSecondsRef.current > 0) {
        const pausedAt = countdownPausedAtRef.current
        const pausedSeconds = countdownPausedSecondsRef.current
        const elapsedSeconds = Math.floor((Date.now() - pausedAt) / 1000)
        const adjustedRemainingSeconds = Math.max(0, pausedSeconds - elapsedSeconds)

        if (adjustedRemainingSeconds > 0 && transaction.orderId) {
          paymentCountdown.setRemainingSeconds(adjustedRemainingSeconds)
          paymentCountdown.start()
          setIsCheckingPayment(true)
          setNetworkError(false)
          countdownPausedAtRef.current = null
          countdownPausedSecondsRef.current = 0
          logActivity('Network reconnected during payment', {
            orderId: transaction.orderId,
            adjustedRemainingSeconds,
            elapsedSeconds
          })
        } else {
          // Time expired while offline, check payment status
          countdownPausedAtRef.current = null
          countdownPausedSecondsRef.current = 0
          checkPaymentStatus()
        }
      } else if (networkError && transaction.orderId) {
        // Network error was set but no countdown was paused - just resume polling
        setIsCheckingPayment(true)
        setNetworkError(false)
        logActivity('Network reconnected during payment - resuming status checks', {
          orderId: transaction.orderId
        })
      }
    }
  }, [isOnline])

  useInterval(
    async () => {
      await checkPaymentStatus()
    },
    isCheckingPayment && isOnline ? 5000 : null
  )

  // Grace after 0:00. The main poll runs every 5 s, so a guest who pays in the
  // last seconds is regularly confirmed by the gateway just AFTER the countdown
  // ends — and the booth used to stop asking at exactly that moment, reset
  // itself, and leave the payment recorded with nobody served. Keep asking for
  // the few seconds the auto-reset gives us; the paid branch navigates onward.
  const [graceChecksDone, setGraceChecksDone] = useState(0)
  // Presses of "I have paid, check again" on the current expired screen.
  const [manualRechecks, setManualRechecks] = useState(0)
  useEffect(() => {
    if (!isExpired) {
      setGraceChecksDone(0)
      setManualRechecks(0)
    }
  }, [isExpired])
  useInterval(
    async () => {
      setGraceChecksDone((done) => done + 1)
      await checkPaymentStatus()
    },
    shouldKeepWatchingAfterExpiry({
      isExpired,
      isOnline,
      status: transaction.status,
      checksDone: graceChecksDone
    })
      ? EXPIRY_GRACE_POLL_MS
      : null
  )

  const checkPaymentStatus = async (isButtonClick: boolean = false): Promise<void> => {
    // Read the transaction live instead of the one captured when this function
    // was created. The countdown's completion handler is bound once, so at 0:00
    // it ran an old copy of this function whose `transaction` was still the
    // first render's empty one: the guard below returned immediately and the
    // booth declared the session expired without ever asking the server. That
    // is the last check a guest paying in the final seconds gets.
    const liveTransaction = useSessionStateStore.getState().transaction
    if (!liveTransaction.expiryTime || !liveTransaction.serverTime || !isMounted.current) {
      return
    }

    // Don't check payment status if offline
    if (!isOnline) {
      if (isButtonClick) {
        setNetworkError(true)
        cogoToast.error(t('payment.networkError', 'Network error occurred'))
      }
      return
    }

    try {
      const askedOrderId = liveTransaction.orderId
      const paymentData = await MidtransUtils.checkPaymentStatus(
        askedOrderId || 'order-default-id',
        { reference: pointReferenceRef.current }
      )

      // Drop an answer about an order the page is no longer showing. Coming back
      // online after the window ran out, the page mints a new charge AND asks
      // about the old one in the same breath; if the new charge is set up first,
      // the old order's "expired" would otherwise land on it and the booth would
      // show a brand-new QR as expired and reset.
      if (!isAnswerForCurrentOrder(askedOrderId, useSessionStateStore.getState().transaction?.orderId)) {
        return
      }

      // What the terminal is doing. Drives the card panel, and the rules at
      // 0:00 below, which follow the order rather than the booth's clock.
      if (paymentTypeRef.current === 'terminal') {
        const nextPointStatus = normalizePointStatus(paymentData.pointStatus)
        setPointStatusLive(nextPointStatus)
        // Declined, cancelled on the device or expired: nothing left on the
        // terminal for the leftover sweep to cancel.
        if (pointCountdownAction(nextPointStatus) === 'expire') {
          forgetOpenTerminalOrder(liveTransaction.orderId)
        }
        // The booth's window is already over and the terminal is done with the
        // order: declined, cancelled on the device, or expired. The countdown
        // handler has had its turn, so nothing else would ever end this
        // session, and `isExpired` from the gateway only covers the last of
        // the three.
        if (
          pointWindowOverRef.current &&
          pointCountdownAction(nextPointStatus) === 'expire' &&
          useSessionStateStore.getState().transaction?.status !== 'expire'
        ) {
          safeUpdate(() => {
            setIsCheckingPayment(false)
            setIsExpired(true)
            setCardHold(false)
            logActivity('Card terminal order ended after the payment window', {
              orderId: liveTransaction.orderId,
              pointStatus: nextPointStatus
            })
            setSessionState({ transaction: { status: 'expire' } })
            stateUploader.uploadSessionState()
          })
          return
        }
      }

      // If payment has expired, update the UI
      if (paymentData.isExpired) {
        // The grace checks below keep asking after 0:00, and the answer stays
        // "expired" until a late payment lands. Record it once: re-writing the
        // status and re-uploading the session on every grace check is noise.
        const alreadyExpired =
          useSessionStateStore.getState().transaction?.status === 'expire'
        safeUpdate(() => {
          setIsCheckingPayment(false)
          setIsExpired(true)
          // A guest who pressed "I have paid" deserves an answer, not silence.
          if (isButtonClick && isOnlineRef.current) {
            cogoToast.info(
              t('payment.notReceivedYet', 'Payment not received yet. Please check your phone.')
            )
          }
          if (alreadyExpired) return
          logActivity('Payment expired.', {
            orderId: liveTransaction.orderId,
            expiryTime: liveTransaction.expiryTime,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus,
            paymentType
          })
          setSessionState({
            transaction: {
              status: 'expire'
            }
          })
          stateUploader.uploadSessionState()
        })
        return
      }

      if (paymentData.paid) {
        forgetOpenTerminalOrder(liveTransaction.orderId)
        safeUpdate(async () => {
          setIsCheckingPayment(false)
          cogoToast.success(t('payment.successMessage', 'Payment successful!'))
          logActivity('Payment successful.', {
            orderId: liveTransaction.orderId,
            serverTime: paymentData.serverTime,
            transactionStatus: paymentData.transactionStatus,
            paymentType
          })
          setSessionState({
            transaction: {
              status: 'settlement',
              // Recorded in the same words as the staff-confirmed card, because
              // to the guest it was one card payment. `collectedBy: 'gateway'`
              // is the difference that matters for the money: this one ran
              // through the operator's own gateway, so it has an order to
              // reconcile against and an earning, where the manual flow has
              // neither. `terminalRef` is that order id.
              ...(paymentTypeRef.current === 'terminal'
                ? {
                    paymentMethod: 'card_terminal' as const,
                    collectedBy: 'gateway' as const,
                    terminalRef: pointReferenceRef.current || undefined,
                    // The serial first: `name` is the label the operator typed
                    // for the EDC they run by hand, and a booth that switched
                    // modes would otherwise file Point sessions under it.
                    terminalName:
                      terminalSerial(cardTerminal.terminalId || '') || cardTerminal.name
                  }
                : {})
            }
          })
          stateUploader.uploadSessionState()
          const filterSettingsForPayment = globalConfig?.pages.filter.settings
          const applyAfterCaptureForPayment = filterSettingsForPayment?.applyAfterCapture || false
          // Camera selection already happened before checkout — see handleNext.
          const nextPage = welcomeSettings?.startWithPayment
            ? applyAfterCaptureForPayment
              ? 'frame'
              : 'filter'
            : 'capture'
          navigate(`/${projectId}/${nextPage}`)
        })
      } else if (isButtonClick) {
        safeUpdate(() => {
          if (isOnlineRef.current) {
            cogoToast.error(t('payment.notSuccessful', 'Payment not successful. Please try again.'))
          }
        })
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
      if (isButtonClick) {
        safeUpdate(() => {
          if (isOnlineRef.current) {
            cogoToast.error((e as Error).message)
          }
        })
      }
    }
  }

  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('hasSeenSkipPaymentGuide')
    if (!hasSeenGuide) {
      setShowSkipGuide(true)
      localStorage.setItem('hasSeenSkipPaymentGuide', 'true')

      // Auto-hide the tooltip after 10 seconds
      const timer = setTimeout(() => {
        setShowSkipGuide(false)
      }, 10000)

      return (): void => clearTimeout(timer)
    }
    return
  }, [])

  // A held session goes back to the QR after five minutes — long enough for staff
  // to walk over and run a card, short enough that a walk-away frees the booth.
  useEffect(() => {
    if (!cardHold) return
    // A connected terminal has its own clock (the order's expiry, and the
    // booth countdown below), and dropping its panel would leave the guest
    // paying on a terminal the screen no longer mentions.
    if (paymentType === 'terminal') return
    const timer = setTimeout(() => setCardHold(false), 5 * 60 * 1000)
    return (): void => clearTimeout(timer)
  }, [cardHold, paymentType])

  /**
   * Pick the next page after a successful customer voucher / operator PIN
   * settles the transaction. Lifted out so both paths route identically.
   * Mirrors the navigation choice in `handleCodeEntry` below.
   */
  const navigateAfterSettlement = async (): Promise<void> => {
    const customPath = welcomeSettings?.customPath
    const filterSettings = globalConfig?.pages.filter.settings
    const applyAfterCapture = filterSettings?.applyAfterCapture || false
    // Camera selection already happened before checkout — see handleNext.
    const nextPage =
      customPath === 'booth-daughter'
        ? 'capture'
        : welcomeSettings?.startWithPayment
          ? applyAfterCapture
            ? 'frame'
            : 'filter'
          : 'capture'
    navigate(`/${projectId}/${nextPage}`)
  }

  /**
   * Customer voucher path on the Payment page (NOT the operator-PIN
   * bypass). Validates against `/api/promos/apply`; cash voucher settles
   * the transaction and navigates forward, discount voucher displays the
   * residual amount and asks the customer to settle offline with the
   * operator. Same contract as view/Checkout's `handlePromoEnter`.
   */
  const requestCardPayment = (): void => {
    logActivity('Card payment requested', {
      orderId: transaction.orderId,
      amount: transaction.nativeAmount || transaction.amount,
      cardMode: cardTerminal.mode
    })
    setCardHold(true)
    if (connectedCard) void startTerminalOrder()
  }

  /** Connected mode: put the amount on the terminal. */
  const startTerminalOrder = async (): Promise<void> => {
    setTerminalBusy(true)
    try {
      await initiatePayment({
        terminalId: cardTerminal.terminalId,
        ...(nayaxTerminal ? { terminalProvider: 'nayax' as const } : {})
      })
      // Offline, or the call failed: there is no order to follow, so drop the
      // panel and leave the guest on the payment screen with its own error
      // state. The staff-confirm face belongs to the manual mode alone.
      if (paymentTypeRef.current !== 'terminal') setCardHold(false)
    } finally {
      setTerminalBusy(false)
    }
  }

  /**
   * Ask Studio to cancel the order on the terminal. Mercado Pago only allows
   * that while the terminal has not picked it up, so the answer decides
   * whether the booth may move: `at_terminal` means a customer is paying.
   */
  const cancelTerminalOrder = async (): Promise<PointCancelOutcome> => {
    const live = useSessionStateStore.getState().transaction
    if (!live?.orderId) return 'skipped'
    if (pointCancelInFlightRef.current) return 'unknown'
    pointCancelInFlightRef.current = true
    try {
      const outcome = await MidtransUtils.cancelPayment(live.orderId, {
        reference: pointReferenceRef.current
      })
      logActivity('Card terminal order cancel', { orderId: live.orderId, outcome })
      if (canLeaveAfterCancel(outcome)) {
        setPointStatusLive('canceled')
        forgetOpenTerminalOrder(live.orderId)
      } else if (outcome === 'at_terminal') setPointStatusLive('at_terminal')
      return outcome
    } finally {
      pointCancelInFlightRef.current = false
    }
  }

  /**
   * Back to the payment code. The order on the terminal has to go first: it is
   * still showing the amount, and a guest who taps a card after the booth has
   * moved on would pay for a session nobody is watching.
   */
  const leaveCardPanel = async (): Promise<void> => {
    if (paymentTypeRef.current !== 'terminal') {
      setCardHold(false)
      return
    }
    setTerminalBusy(true)
    try {
      const outcome = await cancelTerminalOrder()
      if (!canLeaveAfterCancel(outcome)) {
        cogoToast.error(
          outcome === 'processed'
            ? t('payment.card.terminalAlreadyPaid', 'That payment went through. One moment.')
            : t(
                'payment.card.terminalCancelOnDevice',
                'Cancel the payment on the terminal first.'
              )
        )
        return
      }
      setCardHold(false)
      // Studio replaced this session's charge with the terminal order, so the
      // code that was on screen is spent. Mint a fresh one.
      await initiatePayment()
    } finally {
      setTerminalBusy(false)
    }
  }

  const beginCardConfirm = (): void => {
    // Same gate as skipping payment: the booth PIN if one is set, otherwise straight in.
    if (password) setShowCardPin(true)
    else setShowCardCode(true)
  }

  const handleCardPin = (pin: string): void => {
    if (pin !== password) {
      cogoToast.error(t('controls.wrongPin', 'Oops! Wrong PIN. Please try again.'))
      return
    }
    setShowCardPin(false)
    setShowCardCode(true)
  }

  const handleCardConfirm = async (rawCode: string): Promise<void> => {
    const code = (rawCode || '').trim()
    if (cardTerminal.requireApprovalCode && !code) {
      setCardCodeError(t('payment.card.codeRequired', 'Enter the approval code from the receipt.'))
      return
    }
    const amount = transaction.nativeAmount || transaction.amount || getDefaultPrice()
    logActivity('Card terminal payment confirmed', {
      orderId: transaction.orderId,
      amount,
      terminalName: cardTerminal.name,
      approvalCode: code ? 'set' : 'none'
    })
    setShowCardCode(false)
    setCardHold(false)
    setSessionState({
      transaction: {
        status: 'settlement',
        // The card ran on the operator's own terminal in the project's currency,
        // so the settlement IS the native price. `transaction.amount` may still
        // hold the gateway charge of a QR minted before staff switched to card
        // (IDR for cross-border QRIS), which paired with this currency recorded
        // RM 6,015 as "26,124,967 MYR". Same shape as the cash-voucher path.
        amount,
        currency: projectCurrency,
        nativeAmount: amount,
        nativeCurrency: projectCurrency,
        fxRate: undefined,
        paymentMethod: 'card_terminal',
        collectedBy: 'operator',
        terminalRef: code || undefined,
        terminalName: cardTerminal.name || undefined,
        confirmedBy: userId || undefined,
        confirmedAt: new Date().toISOString()
      }
    })
    stateUploader.uploadSessionState()
    cogoToast.success(t('payment.card.confirmed', 'Card payment recorded'))
    await navigateAfterSettlement()
  }

  const handleVoucherSubmit = async (code: string): Promise<void> => {
    const trimmed = (code || '').trim()
    if (!trimmed) {
      setVoucherError(t('checkout.invalidPromo', 'Invalid promo code'))
      return
    }
    setVoucherError('')
    setIsVoucherLoading(true)
    try {
      // `/api/promos/apply` expects the subtotal in the project's NATIVE
      // currency (voucher `value`/`minPurchase`/`maxPurchase` are stored
      // ringgit-shape for MYR projects after the Rp→RM migration). Prefer
      // `nativeAmount` over `transaction.amount` — the latter holds the
      // gateway SETTLEMENT amount (IDR for cross-border QRIS) once a
      // payment attempt has settled the session writer below. Today this
      // path is only reachable in voucher-only regions where no API call
      // has fired, but the defensive fallback chain keeps it safe if the
      // flow ever changes.
      const subtotal = Math.max(
        0,
        transaction.nativeAmount || transaction.amount || getDefaultPrice()
      )
      const response = await fetch(`${process.env.VITE_API_URL}/api/promos/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: trimmed,
          projectId: projectId || '',
          amount: Math.round(subtotal)
        })
      })
      const data = await response.json().catch(() => ({}))
      const isValid = response.ok && data.isValid
      const isCash = Boolean(isValid && data.isCash)
      if (!isValid) {
        const msg =
          data.message || data.error || t('checkout.invalidPromo', 'Invalid promo code')
        setVoucherError(msg)
        cogoToast.error(msg)
        return
      }
      if (isCash) {
        logActivity('Payment cash voucher applied', { code: trimmed, amount: subtotal })
        cogoToast.success(t('checkout.promoApplied', 'Promo code applied successfully!'))
        setShowVoucherInput(false)
        setSessionState({
          transaction: {
            status: 'settlement',
            currency: projectCurrency,
            voucherCode: trimmed,
            voucherId: data.promoId,
            voucherType: 'cash',
            voucherValue: data.value
          }
        })
        stateUploader.uploadSessionState()
        await navigateAfterSettlement()
        return
      }
      // Discount voucher: we can't open a gateway in voucher-only mode,
      // so surface the residual and ask the customer to settle offline
      // with the operator. Matches the reprint-modal contract.
      const apiDiscount = Math.round(Number(data.discount) || 0)
      const finalAmount = Math.max(0, Math.round(subtotal - apiDiscount))
      logActivity('Payment discount voucher applied (operator settlement)', {
        code: trimmed,
        discount: apiDiscount,
        finalAmount
      })
      cogoToast.info(
        t(
          'payment.voucherDiscountedSettleOffline',
          'Voucher applied. Please settle {{amount}} with the operator to continue.',
          { amount: formatAmount(finalAmount, projectCurrency) }
        )
      )
    } catch (error) {
      console.error('Voucher validation error:', error)
      const msg = t(
        'checkout.promoValidationFailed',
        'Failed to validate promo code. Please try again.'
      )
      setVoucherError(msg)
      cogoToast.error(msg)
    } finally {
      setIsVoucherLoading(false)
    }
  }

  const handleCodeEntry = async (pin: string): Promise<void> => {
    if (pin === password) {
      // Check if it's booth-daughter custom path
      const customPath = welcomeSettings?.customPath
      const filterSettings = globalConfig?.pages.filter.settings
      const applyAfterCapture = filterSettings?.applyAfterCapture || false
      // Camera selection already happened before checkout — see handleNext.
      const nextPage =
        customPath === 'booth-daughter'
          ? 'capture'
          : welcomeSettings?.startWithPayment
            ? applyAfterCapture
              ? 'frame'
              : 'filter'
            : 'capture'
      useSessionStateStore
        .getState()
        .logActivity('Payment bypassed with code in offline mode', { to: nextPage })
      navigate(`/${projectId}/${nextPage}`)
      setShowCodeEntry(false)
    } else {
      cogoToast.error(t('payment.wrongCode', 'Incorrect code. Please try again.'))
    }
  }

  const handleRetry = async (): Promise<void> => {
    // Check network status first
    if (!isOnline) {
      cogoToast.error(t('payment.offline', 'No internet connection'))
      return
    }

    setIsRetrying(true)
    setNetworkError(false)

    logActivity('Payment retry initiated', {
      isOnline,
      orderId: transaction.orderId
    })

    try {
      await initiatePayment()
    } catch (error) {
      console.error('Error retrying payment:', error)
    } finally {
      setIsRetrying(false)
    }
  }

  // The card panel has two faces. `terminalBusy` covers the moment between
  // the guest pressing the button and Studio answering, so the panel does not
  // flash the staff instructions at a booth that has a connected terminal.
  const terminalCard = paymentType === 'terminal' || (connectedCard && terminalBusy)
  const terminalMessage = pointStatusMessage(pointStatus, pointWindowOver)

  const renderPaymentInterface = (): JSX.Element => {
    // Show network error state if offline and payment was already initiated
    if (networkError && !isOnline && transaction.orderId) {
      return (
        <div className={`flex w-full max-w-sm flex-col items-center ${paymentTight ? 'gap-3' : 'gap-5'}`}>
          <div className="rounded-full bg-amber-100/90 p-4">
            <FaCreditCard className={`text-amber-700 ${paymentTight ? 'h-10 w-10' : 'h-14 w-14'}`} />
          </div>
          <div className="w-full text-center text-carbon">
            <p className={`font-semibold ${paymentTight ? 'text-base' : 'text-lg'}`}>
              {t('payment.offline', 'No internet connection')}
            </p>
            <p className={`mt-1 text-gray-600 ${paymentNarrowLandscape ? 'text-xs' : 'text-sm'}`}>
              {t('payment.offlineMessage', 'Please check your connection and try again.')}
            </p>
          </div>
          <div className={`flex w-full flex-col items-stretch ${paymentTight ? 'gap-2' : 'gap-3'}`}>
            <Button
              text={t('payment.reload', 'Reload')}
              onClick={handleRetry}
              disabled={isRetrying}
              fullWidth
              className={paymentBtnCompact}
            />
            <Button
              text={t('payment.enterCode', 'Enter code')}
              onClick={() => setShowCodeEntry(true)}
              variant="secondary"
              fullWidth
              className={paymentBtnCompact}
            />
            <Button
              text={t('controls.cancelSession', 'Cancel session')}
              onClick={() => sessionReset('operator_cancel_offline')}
              variant="secondary"
              fullWidth
              className={paymentBtnCompact}
            />
          </div>
        </div>
      )
    }

    // Show offline mode UI when offline and no payment has been initiated
    if (!isOnline && !transaction.orderId) {
      return (
        <div className={`flex w-full max-w-sm flex-col items-center ${paymentTight ? 'gap-3' : 'gap-4'}`}>
          <div
            className="rounded-full p-4"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <FaCreditCard
              className={paymentTight ? 'h-10 w-10' : 'h-14 w-14'}
              style={{ color: primaryColor }}
            />
          </div>
          <div className="text-center text-carbon">
            <p className={`font-semibold ${paymentTight ? 'text-base' : 'text-lg'}`}>
              {t('payment.offlineMode', 'Offline')}
            </p>
            <p className={`mt-1 text-gray-600 ${paymentNarrowLandscape ? 'text-xs' : 'text-sm'}`}>
              {t('payment.offlineInstructionShort', 'Enter your operator code to continue.')}
            </p>
          </div>
          <div className={`flex w-full flex-col items-stretch ${paymentTight ? 'gap-2' : 'gap-3'}`}>
            <Button
              text={t('payment.enterCode', 'Enter code')}
              onClick={() => setShowCodeEntry(true)}
              fullWidth
              className={paymentBtnCompact}
            />
            <Button
              text={t('controls.cancelSession', 'Cancel session')}
              onClick={() => sessionReset('operator_cancel_offline')}
              variant="secondary"
              fullWidth
              className={paymentBtnCompact}
            />
          </div>
        </div>
      )
    }

    if (isExpired) {
      const handleRestart = (): void => {
        autoResetCountdown.stop()
        sessionReset('payment_expired_restart')
      }
      // A guest who finished paying just too late is standing right there with
      // the receipt on their phone. Give them a way to say so: re-ask the
      // server, and put the auto-reset back to the top so the answer can land.
      const handleCheckAgain = (): void => {
        // Capped: each press restarts the auto-reset, so unlimited presses
        // would let anyone hold a public booth on this screen forever.
        if (!canCheckAgainManually(manualRechecks)) return
        setManualRechecks((presses) => presses + 1)
        autoResetCountdown.stop()
        autoResetCountdown.setRemainingSeconds(AUTO_RESET_SECONDS)
        autoResetCountdown.start()
        setGraceChecksDone(0)
        logActivity('Guest asked to re-check an expired payment', {
          orderId: transaction.orderId,
          press: manualRechecks + 1
        })
        void checkPaymentStatus(true)
      }
      return (
        <div className={`flex w-full max-w-sm flex-col items-center ${paymentTight ? 'gap-3' : 'gap-4'}`}>
          <div
            className="rounded-full p-4"
            style={{ backgroundColor: `${primaryColor}18` }}
          >
            <FaUndo
              className={paymentTight ? 'h-10 w-10' : 'h-12 w-12'}
              style={{ color: primaryColor }}
            />
          </div>
          <div className="w-full text-center text-carbon">
            <p className={`font-semibold ${paymentTight ? 'text-base' : 'text-lg'}`}>
              {t('payment.expired', 'Payment code expired')}
            </p>
            <p className={`mt-1 text-gray-600 ${paymentNarrowLandscape ? 'text-xs' : 'text-sm'}`}>
              {t('payment.autoRestart', 'Auto-restart in')}{' '}
              <span className="font-mono tabular-nums text-carbon">{remainingTime}</span>
            </p>
          </div>
          {canCheckAgainManually(manualRechecks) && (
            <Button
              text={t('payment.checkAgain', 'I have paid, check again')}
              onClick={handleCheckAgain}
              fullWidth
              className={paymentBtnCompact}
            />
          )}
          <Button
            text={t('payment.restart', 'Restart')}
            onClick={handleRestart}
            fullWidth
            className={paymentBtnCompact}
          />
        </div>
      )
    }

    // Voucher-only region: no QR will ever load — show the customer
    // voucher-entry CTA in the QR's slot so the page actually offers a
    // way forward. (Standard flow's redirect from Payment → Checkout was
    // dropped: the event-template flow `startWithPayment: true` goes
    // Welcome → Payment directly and has no Checkout page in its
    // pipeline, so bouncing the customer there is jarring. Putting the
    // input in-place works in both flows.)
    if (isVoucherOnlyRegion) {
      return (
        <div
          className={`flex w-full max-w-sm flex-col items-center text-center ${paymentTight ? 'gap-3' : 'gap-4'}`}
        >
          <div
            className="rounded-full p-4"
            style={{ backgroundColor: `${primaryColor}18` }}
          >
            <FaCreditCard
              className={paymentTight ? 'h-10 w-10' : 'h-14 w-14'}
              style={{ color: primaryColor }}
            />
          </div>
          <Button
            text={t('checkout.enterVoucher', 'Enter Voucher Code')}
            onClick={() => {
              setVoucherError('')
              setShowVoucherInput(true)
            }}
            fullWidth
            className={paymentBtnCompact}
          />
        </div>
      )
    }

    if (paymentType === 'core' && qrUrl) {
      return <QrCodeImage
          qrUrl={qrUrl}
          className="w-full h-full object-contain"
          onEvent={qrActivityLogger(logActivity, 'view-payment')}
        />
    }

    if (paymentType === 'snap' && snapToken) {
      return (
        <div className={`flex w-full max-w-sm flex-col items-center ${paymentTight ? 'gap-3' : 'gap-4'}`}>
          <div
            className="rounded-full p-4"
            style={{ backgroundColor: `${primaryColor}18` }}
          >
            <FaCreditCard
              className={paymentTight ? 'h-10 w-10' : 'h-14 w-14'}
              style={{ color: primaryColor }}
            />
          </div>
          <div className="text-center text-carbon">
            <p className={`font-semibold ${paymentTight ? 'text-base' : 'text-lg'}`}>
              {t('payment.snapReady', 'Ready to pay')}
            </p>
            <p className={`mt-1 text-gray-600 ${paymentNarrowLandscape ? 'text-xs' : 'text-sm'}`}>
              {t('payment.snapInstructionShort', 'Open the secure window to complete payment.')}
            </p>
          </div>
          <Button
            text={
              snapPaymentOpened
                ? t('payment.snapOpening', 'Opening…')
                : t('payment.openSnap', 'Open payment')
            }
            onClick={openSnapPayment}
            disabled={snapPaymentOpened}
            fullWidth
            className={paymentBtnCompact}
          />
        </div>
      )
    }

    if (paymentType === 'redirect' && redirectUrl) {
      // Straight-redirect flow: the fullscreen frameless ToyyibPay popup
      // is opened automatically via the auto-open useEffect, so users
      // never need to interact with the page underneath. We only render
      // a minimal Spinner here (which the popup covers anyway). The only
      // exception is a hard failure to open the popup — in that case
      // surface the error with a retry button so the user isn't stuck
      // on a forever-spinner with no way out.
      if (billWindowError) {
        return (
          <div
            className={`flex w-full max-w-sm flex-col items-center text-center ${paymentTight ? 'gap-3' : 'gap-4'}`}
          >
            <div
              className="rounded-full p-4"
              style={{ backgroundColor: `${primaryColor}18` }}
            >
              <FaCreditCard
                className={paymentTight ? 'h-10 w-10' : 'h-14 w-14'}
                style={{ color: primaryColor }}
              />
            </div>
            <p
              className={`text-red-600 ${paymentNarrowLandscape ? 'text-xs' : 'text-sm'}`}
            >
              {billWindowError}
            </p>
            <Button
              text={t('payment.popupRetry', 'Retry')}
              onClick={() => openBillWindow()}
              disabled={billWindowOpen}
              fullWidth
              className={paymentBtnCompact}
            />
          </div>
        )
      }
      return <Spinner />
    }

    // A terminal payment has nothing to draw: the card panel above is what the
    // guest reads. If anything ever drops that panel while the order is live,
    // say what the terminal is doing instead of spinning at them forever,
    // which is what an unknown payment type does here.
    if (paymentType === 'terminal') {
      return (
        <div
          className={`flex w-full max-w-sm flex-col items-center text-center ${paymentTight ? 'gap-3' : 'gap-4'}`}
        >
          <div className="rounded-full p-4" style={{ backgroundColor: `${primaryColor}18` }}>
            <FaCreditCard
              className={paymentTight ? 'h-10 w-10' : 'h-14 w-14'}
              style={{ color: primaryColor }}
            />
          </div>
          <p className={`text-carbon ${paymentTight ? 'text-sm' : 'text-base'}`} role="status">
            {t(terminalMessage.key, terminalMessage.fallback)}
          </p>
        </div>
      )
    }

    return <Spinner />
  }

  return (
    <BackgroundContainer
      className={`flex h-full min-h-0 items-center justify-center ${paymentNarrowLandscape ? 'overflow-hidden p-2' : 'p-4'}`}
      data-page="payment"
    >
      {showSkipGuide && (
        <div
          className={`absolute z-50 max-w-xs rounded-lg bg-gray-200 shadow-lg ${paymentTight ? 'top-8 right-4 p-3' : 'top-16 right-12 p-4'}`}
        >
          <div className="relative">
            <div className="absolute -top-6 right-4 w-4 h-4 bg-gray-200 transform rotate-45" />
            <p className="text-sm text-gray-700">
              {t(
                'payment.skipGuide',
                'Need to skip payment? Click the money icon in the top right corner and enter the PIN.'
              )}
            </p>
            <button
              onClick={(): void => setShowSkipGuide(false)}
              className="absolute -top-2 -right-2 text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        </div>
      )}
      {/*
        Straight-redirect flow (currently toyyibPay): pressing
        "Proceed to Payment" on Checkout should land users directly on the
        gateway's hosted bill page. The fullscreen frameless popup is the
        only UI they should see, so we hide the entire payment panel (logo
        / amount / timer / gateway tagline). Polling, countdown and the
        navigation-on-settlement hooks still run regardless of what's
        rendered.

        We gate this on the actual returned flow rather than on the user's
        email so that if the backend ever falls back to a different payment
        type for a Malaysian user (e.g. Midtrans core/QR), they still get
        the full QR UI instead of being stuck on a spinner.

        While the create-bill request is in flight (paymentType still at
        its default 'core' and no qrUrl yet), keep the chrome hidden for
        Malaysian users so the brief loading moment matches the popup
        background, not the QRIS panel.

        The panel re-appears for recoverable states — popup-open failure
        or session expiry — so the user always has a way to retry/restart.
      */}
      {((): boolean => {
        const isToyyibpayRedirect =
          activeProvider === 'toyyibpay' && paymentType === 'redirect'
        // Initial loading window: `/api/payments` is still in flight and we
        // don't yet know which gateway will be returned. Hide the QRIS-
        // themed panel for everyone during this window so that non-QRIS
        // rails (today: ToyyibPay → redirect popup) never flash the QRIS
        // logo / countdown / empty QR box on the way to the gateway's
        // hosted page. QRIS rails just see the panel appear once we know
        // it's a QRIS rail; the QR slot is then ready almost immediately.
        //
        // Gate this only when there's actually a payment call to wait for —
        // voucher-only regions, offline mode and the network-error retry
        // panel all manage their own UI and must keep rendering inside the
        // chrome (they each have a working `paymentInitialized` flip in
        // `initiatePayment` so this never gets stuck).
        const isAwaitingPaymentInit =
          !paymentInitialized &&
          !isVoucherOnlyRegion &&
          isOnline &&
          !networkError &&
          transaction.status !== 'settlement'
        const useStraightRedirectUI =
          (isToyyibpayRedirect || isAwaitingPaymentInit) &&
          !isExpired &&
          !billWindowError
        return useStraightRedirectUI
      })() ? (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <GlassPanel className={`${paymentGlass} text-carbon`}>
          <div className={`flex items-center ${paymentTight ? 'gap-2' : 'gap-4'}`}>
            {/*
              QRIS logo only renders when the customer is actually going
              to use QRIS — that's Midtrans (default Dreambooth or the
              operator's custom Midtrans wallet) on the QR rail. Hidden
              for voucher-only regions (no payment happens), ToyyibPay
              (uses DuitNow QR, not QRIS), Razorpay (UPI QR) and Snap
              (multi-method form). Mid-load on a non-Midtrans rail we also
              hide it. Same rule as `guestRailLabel` above.
            */}
            {paymentType === 'core' &&
              guestRail === 'qris' && (
                <img
                  src={'/assets/images/qris.png'}
                  alt={t('payment.qrisLogoAlt', 'QRIS logo')}
                  className={paymentTight ? 'h-5' : 'h-8'}
                />
              )}
            <h1 className={`font-medium text-carbon ${paymentTitle}`}>{globalConfig?.title}</h1>
          </div>
          {((): React.ReactElement => {
            // Show the project-native price (e.g. RM 19.00) as the headline.
            // `transaction.amount` is the gateway SETTLEMENT amount after
            // `/api/payments` resolves — IDR ~67014 on cross-border QRIS for
            // an MYR project — and rendering that with the project's symbol
            // produces "RM 67,014", a 3500× price-anxiety bug that drives
            // customers to retry-pay repeatedly. `transaction.nativeAmount`
            // is preserved exactly for this display path; fall back to
            // `transaction.amount` for the pre-`/api/payments` window
            // (where it's still the native value from Checkout) and to
            // `getDefaultPrice()` as a last resort.
            //
            // Only the project's own currency is shown. Cross-border QRIS bills
            // IDR, but a rupiah line under "RM 15.00" tells a Malaysian guest the
            // booth is Indonesian; the guest's banking app already shows what it
            // charges, and the settlement figure stays on the session record
            // (`amount`/`currency`/`fxRate`) for the dashboard's reconciliation.
            const nativeAmount =
              transaction.nativeAmount || transaction.amount || getDefaultPrice()
            return (
              <div className="flex flex-col items-center">
                <h1 className={`font-bold text-carbon ${paymentTitle}`}>
                  {formatAmount(nativeAmount, projectCurrency)}
                </h1>
              </div>
            )
          })()}
          <div
            className={`relative flex items-center justify-center ${paymentType === 'redirect' ? paymentRedirectBox : paymentQrBox}`}
          >
            {cardHold ? (
              <div className={`flex w-full max-w-sm flex-col items-center ${paymentTight ? 'gap-3' : 'gap-4'}`}>
                {terminalCard ? (
                  <>
                    <div className="w-full rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-left text-blue-900">
                      <p className={`font-semibold ${paymentTight ? 'text-sm' : 'text-base'}`}>
                        <FaCreditCard className="mr-2 inline-block align-middle" aria-hidden="true" />
                        {t('payment.card.terminalTitle', 'Pay on the card terminal')}
                      </p>
                      <p className={`mt-1 ${paymentTight ? 'text-xs' : 'text-sm'}`} role="status">
                        {t(terminalMessage.key, terminalMessage.fallback)}
                      </p>
                    </div>
                    {transaction.orderId && (
                      <p className="font-mono text-xs text-gray-500">{transaction.orderId}</p>
                    )}
                    {!pointWindowOver && remainingTime !== COUNTDOWN_PLACEHOLDER && (
                      <p
                        className={`${paymentRemainingTimeClass} ${
                          isCountdownUrgent(remainingTime) ? '!font-bold !text-red-600' : ''
                        }`}
                      >
                        {remainingTime}
                      </p>
                    )}
                    <div className={`flex w-full flex-col items-stretch ${paymentTight ? 'gap-2' : 'gap-3'}`}>
                      {isPointRetryable(pointStatus) && (
                        <Button
                          text={t('payment.card.terminalTryAgain', 'Try again')}
                          onClick={() => void startTerminalOrder()}
                          disabled={terminalBusy}
                          fullWidth
                          className={paymentBtnCompact}
                        />
                      )}
                      <Button
                        text={t('payment.card.backToPayment', 'Back to {{rail}}', {
                          rail: guestRailLabel
                        })}
                        onClick={() => void leaveCardPanel()}
                        variant="secondary"
                        fullWidth
                        disabled={terminalBusy}
                        className={paymentBtnCompact}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-amber-900">
                      <p className={`font-semibold ${paymentTight ? 'text-sm' : 'text-base'}`}>
                        <span className="mr-2 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle" aria-hidden="true" />
                        {t('payment.card.holdTitle', 'Please ask our staff')}
                      </p>
                      <p className={`mt-1 ${paymentTight ? 'text-xs' : 'text-sm'}`}>
                        {t('payment.card.holdBody', 'Show them this screen — they will run your card on the terminal.')}
                      </p>
                    </div>
                    {transaction.orderId && (
                      <p className="font-mono text-xs text-gray-500">{transaction.orderId}</p>
                    )}
                    <p className={paymentHint}>{t('payment.card.holdHeld', 'Your session is held for 5 minutes.')}</p>
                    <div className={`flex w-full flex-col items-stretch ${paymentTight ? 'gap-2' : 'gap-3'}`}>
                      <Button
                        text={t('payment.card.confirmStaff', 'Confirm card payment (staff)')}
                        onClick={beginCardConfirm}
                        fullWidth
                        className={paymentBtnCompact}
                      />
                      <Button
                        text={t('payment.card.backToPayment', 'Back to {{rail}}', {
                          rail: guestRailLabel
                        })}
                        onClick={() => void leaveCardPanel()}
                        variant="secondary"
                        fullWidth
                        className={paymentBtnCompact}
                      />
                    </div>
                  </>
                )}
              </div>
            ) : (
              renderPaymentInterface()
            )}
          </div>
          {/*
            Hint copy + the polling timer only make sense when an actual
            payment is being awaited. In voucher-only mode nothing is
            being polled; the customer just needs to type the code, so
            we drop both lines to keep the panel focused.
          */}
          {!isVoucherOnlyRegion && !cardHold && (
            <>
              {paymentType === 'core' && !isExpired && (
                <p className={`mt-1 text-center font-medium text-carbon ${paymentTight ? 'text-sm' : 'text-base'}`}>
                  {t('payment.scanInstruction', 'Scan the QR code with your payment app')}
                </p>
              )}
              <p className={`mt-2 ${paymentHint}`}>
                {paymentType === 'core'
                  ? t('payment.automaticCheck', 'Payment status will be checked automatically.')
                  : paymentType === 'snap'
                    ? t('payment.snapCheck', 'Complete payment in the popup window.')
                    : t(
                        'payment.redirectCheck',
                        'Opening secure payment window. Status will update automatically.'
                      )}
              </p>
              {remainingTime !== COUNTDOWN_PLACEHOLDER && (
                <p className={`mt-1 text-center ${paymentHint}`}>
                  {t('payment.timeToPay', 'Complete payment in')}
                </p>
              )}
              <p
                className={`mt-1 ${paymentRemainingTimeClass} ${
                  isCountdownUrgent(remainingTime) ? '!font-bold !text-red-600' : ''
                }`}
              >
                {remainingTime}
              </p>
              {cardEnabled && !isExpired && transaction.status !== 'settlement' && (
                <div className="mt-4 w-full max-w-sm">
                  <div className="mb-2 flex items-center gap-3 text-[11px] uppercase tracking-wide text-gray-400">
                    <span className="h-px flex-1 bg-gray-200" aria-hidden="true" />
                    {t('payment.card.cantScan', "Can't scan?")}
                    <span className="h-px flex-1 bg-gray-200" aria-hidden="true" />
                  </div>
                  <Button
                    text={
                      <span className="flex items-center justify-center gap-2">
                        {t('payment.card.payByCard', 'Pay by card')}
                        {cardTerminal.showCardMarks && (
                          <span className="flex gap-1 opacity-70" aria-hidden="true">
                            <span className="rounded border border-current px-1 font-mono text-[9px] font-semibold tracking-wide">VISA</span>
                            <span className="rounded border border-current px-1 font-mono text-[9px] font-semibold tracking-wide">MC</span>
                          </span>
                        )}
                      </span>
                    }
                    onClick={requestCardPayment}
                    variant="secondary"
                    fullWidth
                    className={paymentBtnCompact}
                  />
                </div>
              )}
              {!isExpired && transaction.status !== 'settlement' && (
                <button
                  type="button"
                  onClick={() => sessionReset('payment_customer_cancel')}
                  className={`mx-auto mt-3 block text-center text-gray-500 underline underline-offset-2 hover:text-gray-700 ${paymentTight ? 'min-h-[44px] text-sm' : 'min-h-[56px] text-base'}`}
                >
                  {t('payment.cancelOrder', 'Cancel and start over')}
                </button>
              )}
            </>
          )}
          {paymentType === 'snap' && (
            <p className="text-sm text-gray-500 text-center">
              {t('payment.snapNote', 'Using secure Midtrans payment gateway')}
            </p>
          )}
          {paymentType === 'redirect' && (
            <p className="text-sm text-gray-500 text-center">
              {t('payment.redirectNote', 'Using secure payment gateway')}{' '}
              <span className="font-medium">{activeProvider.toUpperCase()}</span>
            </p>
          )}
        </GlassPanel>
      )}

      {/*
        Operator-PIN bypass (offline-mode override). 6-digit numeric,
        matched against the operator's settings password. Reserved for
        operator-driven flow — explicitly NOT used for the customer
        voucher-only path (the OnScreenKeyboard below handles that).
      */}
      <PinModal
        isOpen={showCardPin}
        onClose={() => setShowCardPin(false)}
        onSubmit={handleCardPin}
        title={t('payment.card.pinTitle', 'Confirm card payment')}
        subtitle={t('payment.card.pinSubtitle', 'Staff only. Enter the booth PIN.')}
      />
      {showCardCode && (
        <OnScreenKeyboard
          onEnter={handleCardConfirm}
          variant="email"
          modalTitle={t('payment.card.codeTitle', 'Approval code')}
          submitLabel={t('payment.card.codeSubmit', 'Confirm payment')}
          placeholder={
            cardTerminal.requireApprovalCode
              ? t('payment.card.codePlaceholder', 'Approval code from the terminal receipt')
              : t('payment.card.codeOptionalPlaceholder', 'Approval code (optional) — press Enter to confirm')
          }
          error={cardCodeError}
          onInputChange={() => setCardCodeError('')}
          onClose={() => {
            setShowCardCode(false)
            setCardCodeError('')
          }}
        />
      )}
      <PinModal
        isOpen={showCodeEntry}
        onClose={() => setShowCodeEntry(false)}
        onSubmit={handleCodeEntry}
        title={t('payment.codeEntry.title', 'Enter Access Code')}
        subtitle={t(
          'payment.codeEntry.subtitle',
          'Enter 6-digit code to bypass payment in offline mode'
        )}
      />

      {/*
        Customer voucher entry: alphanumeric code validated against
        `/api/promos/apply`. Drives the voucher-only-region flow without
        leaving the Payment page so the event-template (Welcome →
        Payment, no Checkout step) works the same as the standard flow.
      */}
      {showVoucherInput && (
        <OnScreenKeyboard
          onEnter={handleVoucherSubmit}
          placeholder={t(
            'checkout.voucherCodePlaceholder',
            'Type or paste your code'
          )}
          error={voucherError}
          isLoading={isVoucherLoading}
          onInputChange={() => setVoucherError('')}
          onClose={() => {
            setShowVoucherInput(false)
            setVoucherError('')
          }}
        />
      )}
    </BackgroundContainer>
  )
}

export default PaymentPage
